/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { HttpErrorResponse } from '@angular/common/http';
import { ApiError, ApiErrorType, ApiErrorTypeValue } from 'app/types/api-error.type';

/**
 * Helper class for handling API errors
 */
export class ErrorHelper {
    /**
     * Check if error response is a structured API error
     */
    public static isApiError(error: unknown): error is HttpErrorResponse & { error: ApiError } {
        return (
            error instanceof HttpErrorResponse &&
            typeof error.error === 'object' &&
            error.error !== null &&
            'type' in error.error &&
            'message' in error.error &&
            'status' in error.error
        );
    }

    /**
     * Check if error is of a specific type
     */
    public static isErrorType(error: unknown, errorType: ApiErrorTypeValue): boolean {
        if (!ErrorHelper.isApiError(error)) {
            return false;
        }
        return error.error.type === errorType;
    }

    /**
     * Check if error is a validation error
     */
    public static isValidationError(error: unknown): boolean {
        return ErrorHelper.isErrorType(error, ApiErrorType.ValidationError);
    }

    /**
     * Check if error is a conflict error
     */
    public static isConflictError(error: unknown): boolean {
        return ErrorHelper.isErrorType(error, ApiErrorType.Conflict);
    }

    /**
     * Check if error is a not found error
     */
    public static isNotFoundError(error: unknown): boolean {
        return ErrorHelper.isErrorType(error, ApiErrorType.NotFound);
    }

    /**
     * Extract error message from various error formats
     */
    public static getErrorMessage(error: unknown, fallbackMessage = 'An unknown error occurred'): string {
        if (ErrorHelper.isApiError(error)) {
            return error.error.message || error.error.title || fallbackMessage;
        }

        if (error instanceof HttpErrorResponse) {
            // Handle legacy error format
            if (error.error?.message) {
                return error.error.message as string;
            }
            return error.message || fallbackMessage;
        }

        if (error instanceof Error) {
            return error.message;
        }

        return fallbackMessage;
    }

    /**
     * Get full API error details if available
     */
    public static getApiError(error: unknown): ApiError | null {
        if (ErrorHelper.isApiError(error)) {
            return error.error;
        }
        return null;
    }
}
